import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  ConversationParticipantEntity,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { getFriendlyConversationId, getFullName } from '@provision/formatting-utils';
import { portalPaths } from '@provision/portal-routes';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { isNullish } from 'remeda';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import {
  Address,
  Client,
  getPreparedConversationParticipantOptionGroup,
  mapAddress,
  mapClient,
  PreparedConversationParticipantOption,
  useCreateConversation,
  useCurrentUserProfile,
} from 'src/graphql';
import { NullableDeepPartial } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState, useShowModals } from 'src/store/modals';
import { AddressRaw, ClientRaw } from 'src/types';
import { useNavigateRoute } from 'src/utils/routing';
import {
  ConversationFormFields,
  NewConversationFormValues,
  useNewConversationForm,
} from './use-new-conversation-form';

const formStyles = {
  paddingTop: 1,
};

const createPreparedClientOption = (
  client?: NullableDeepPartial<Client>,
): PreparedConversationParticipantOption | undefined => {
  if (!client?.clientId) {
    return undefined;
  }

  const label = (getFullName(client) || '').trim();

  return {
    id: client.clientId,
    value: client.clientId,
    label,
    group: getPreparedConversationParticipantOptionGroup(ConversationParticipantEntity.Client),
    participantEntity: ConversationParticipantEntity.Client,
    conversationParticipantId: client.clientId,
    phoneNumber: client.primaryPhoneNumber ?? '',
    email: client.primaryEmail ?? '',
    entityPartnerId: client.partnerId ?? client.partner?.id ?? undefined,
    avatarFileKey: null,
  };
};

export const AddConversationModal = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { closeModal } = useModalMethods(Modals.AddConversationModal);
  const { isOpened, modalProps } = useModalState(Modals.AddConversationModal);
  const [showAddConversationModal, showAddSiteModal, showAddClientModal] = useShowModals(
    Modals.AddConversationModal,
    Modals.AddSiteModal,
    Modals.AddClientModal,
  );

  const { prevFormValues } = modalProps ?? {};

  const { navigate } = useNavigateRoute();

  const { user } = useCurrentUserProfile();

  const handleSuccessConversationCreate = useCallback(
    (conversationId: string) => {
      navigate(portalPaths.conversation.full(conversationId));
    },
    [navigate],
  );

  const { createConversation, isLoading } = useCreateConversation();

  const defaultFormValues = useMemo(() => {
    return {
      [ConversationFormFields.Partner]: isNullish(selectedPartnerId)
        ? null
        : { id: selectedPartnerId },
      [ConversationFormFields.Group]: null,
      [ConversationFormFields.Requester]: modalProps?.requester ?? null,
      [ConversationFormFields.Site]: modalProps?.site ?? null,
      [ConversationFormFields.ConversationType]: null,
      [ConversationFormFields.Urgency]: null,
      [ConversationFormFields.Subject]: '',
    };
  }, [modalProps?.requester, modalProps?.site, selectedPartnerId]);

  const formMethods = useForm<NewConversationFormValues>({
    defaultValues: prevFormValues || defaultFormValues,
  });

  const { handleSubmit } = formMethods;

  const restoreAddConversationModal = useCallback(
    (options?: { site?: Address; client?: NullableDeepPartial<Client> }) => {
      const { site, client } = options || {};
      const currentFormValues = formMethods?.getValues?.();
      if (currentFormValues) {
        const preparedClientOption = createPreparedClientOption(client);
        showAddConversationModal({
          prevFormValues: {
            ...currentFormValues,
            ...(!!site && {
              [ConversationFormFields.Site]: {
                id: site.addressId,
                label: site.fullAddress ?? '',
              },
            }),
            ...(!!preparedClientOption && {
              [ConversationFormFields.Requester]: {
                id: preparedClientOption.id,
                label: preparedClientOption.label,
                participantEntity: ConversationParticipantEntity.Client,
                data: preparedClientOption,
                email: preparedClientOption.email,
              },
              [ConversationFormFields.Site]: null, // reset site if a new client is added
            }),
          },
        });
      }
    },
    [formMethods, showAddConversationModal],
  );

  const addNewSite = useCallback(
    (client: PreparedConversationParticipantOption) => {
      showAddSiteModal({
        client: {
          partnerId: client.entityPartnerId,
          id: client.id,
          name: client.label,
          email: client.email,
        },
        // reset any associated conversation values if any was
        // present in the modal context
        closeOnSiteCreate: true,
        conversation: undefined,
        onSuccessSubmit: (site: unknown) => {
          restoreAddConversationModal({ site: mapAddress(site as AddressRaw) });
        },
        onCloseModal: () => {
          restoreAddConversationModal();
        },
      });
    },
    [restoreAddConversationModal, showAddSiteModal],
  );

  const addNewClient = useCallback(
    (partnerId: number) => {
      showAddClientModal({
        partnerId,
        onCloseModal: (client?: NullableDeepPartial<ClientRaw>) => {
          restoreAddConversationModal({
            client: client ? mapClient(client as ClientRaw) : undefined,
          });
        },
      });
    },
    [restoreAddConversationModal, showAddClientModal],
  );

  const { formFields } = useNewConversationForm({
    formMethods,
    addNewSite,
    addNewClient,
    disabled: isLoading,
  });

  const onCloseModal = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const onSubmit = useCallback(
    async (values: NewConversationFormValues) => {
      if (
        !user?.id ||
        !values[ConversationFormFields.Partner] ||
        !values[ConversationFormFields.Requester] ||
        !values[ConversationFormFields.ConversationType] ||
        !values[ConversationFormFields.Urgency] ||
        !values[ConversationFormFields.Group]
      ) {
        return;
      }

      const conversationId = crypto.randomUUID();

      onCloseModal();

      const createdConversation = await createConversation({
        conversationId,
        partner: values[ConversationFormFields.Partner],
        subject: values[ConversationFormFields.Subject],
        requesterId: values[ConversationFormFields.Requester].id,
        requesterEmail: values[ConversationFormFields.Requester].email,
        requesterEntity: values[ConversationFormFields.Requester].participantEntity,
        agentId: user.id,
        type: values[ConversationFormFields.ConversationType].id,
        urgencyId: values[ConversationFormFields.Urgency].id,
        groupId: values[ConversationFormFields.Group].id,
        address: values[ConversationFormFields.Site]?.id,
        participationMode: ConversationParticipationMode.Assist,
      });

      const friendlyId = getFriendlyConversationId({
        ...createdConversation!,
        partner: { partnerId: values[ConversationFormFields.Partner].id },
      });

      if (friendlyId) {
        handleSuccessConversationCreate(friendlyId);
      }
    },
    [user?.id, onCloseModal, createConversation, handleSuccessConversationCreate],
  );

  return (
    <Dialog
      open={isOpened}
      onClose={onCloseModal}
      fullWidth
      PaperProps={{
        sx: {
          border: '1px solid',
          borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
        }}
      >
        <Stack direction="row" spacing={2}>
          <Typography variant="overline">Create conversation</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <GenericForm<NewConversationFormValues> formMethods={formMethods} sx={formStyles}>
          {formFields}
        </GenericForm>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit(onSubmit)}
          sx={{
            color: (theme) => theme.palette.custom.OV.sys.primary,
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
